import type { Desktop } from "../core/Desktop.js";
import type { Insets, Size } from "../../common/geometry.js";
import { Panel, type PanelOptions } from "./Panel.js";

/**
 * How a page with a fixed design size fits itself into the room it has.
 *
 * `none` is the default and the one most applications want: no scaling at all, the layout
 * itself adapting through docking, stars and measured children. The other two are for a layout
 * that was drawn at one size and is meant to stay that shape — a kiosk screen, a game HUD, a
 * diagram — where being smaller is better than being rearranged.
 */
export type PageFit = "none" | "contain" | "width";

export interface PageOptions extends PanelOptions {
  /** The size the layout inside was drawn for. Only meaningful with `fit`. */
  design?: Size;
  fit?: PageFit;
}

/**
 * A full-desktop root: one page instead of floating windows.
 *
 * Webwin's usual shape is a desktop with windows on it, which is right for an application that
 * is several things at once. A great many applications are one thing, and for those a window is
 * a frame around the whole screen that the user can only get wrong. A page is that application's
 * root: it sits behind every window, fills the desktop's work area, and is otherwise an ordinary
 * container — bars dock to its edges, panels fill what is left.
 *
 * Keeping it in the background layer rather than making it the desktop itself means a page and a
 * window are not alternatives. A page application can still open a dialog, and the dialog behaves
 * like every other window in the library.
 *
 * ```ts
 * const page = new Page();
 * page.controls.add(new MenuBar({ dock: "top", autoHeight: true }));
 * page.controls.add(new Panel({ dock: "fill" }));
 * page.show(desktop);
 * ```
 */
export class Page extends Panel {
  /** The size the content was drawn for, or null to lay out at whatever size it has. */
  design: Size | null;
  fit: PageFit;

  #desktop: Desktop | null = null;
  #onWorkAreaChanged = (): void => this.#followWorkArea();
  /** True when the application named a background; otherwise the theme names one. */
  #ownBackground: boolean;

  constructor(options: PageOptions = {}) {
    super({ dock: "fill", ...options });
    this.design = options.design ?? null;
    this.fit = options.fit ?? "none";
    this.#ownBackground = options.background !== undefined && options.background !== null;
  }

  /** Put this page behind every window and let it fill the desktop. */
  show(desktop: Desktop): this {
    desktop.addBackground(this);
    return this;
  }

  /**
   * Hold the desktop's reserved chrome clear.
   *
   * A page docks `fill`, so the margin is the one thing standing between it and a taskbar: the
   * docking pass carves the margin out of the desktop's client area before handing over what is
   * left. Following `workareachanged` rather than reading the insets once means a taskbar that
   * appears later still pushes the page out of its way.
   */
  #followWorkArea(): void {
    const desktop = this.#desktop;
    if (!desktop) return;
    const i: Insets = desktop.workAreaInsets;
    const m = this.margin;
    if (m.left === i.left && m.top === i.top && m.right === i.right && m.bottom === i.bottom) return;
    this.margin = { ...i };
  }

  /**
   * Paint the theme's window background unless the application asked for something else.
   *
   * A page is the surface an application is drawn on, so it has to be opaque: every control on
   * it picks its ink from the theme on the assumption of a themed ground, and a transparent page
   * puts that ink on the desktop's wallpaper color instead — which in most themes is dark, and
   * in a light theme makes every caption on the page unreadable.
   */
  #followTheme(): void {
    if (this.#ownBackground) return;
    const theme = this.desktop?.theme;
    if (theme) this.background = theme.palette.windowBg;
  }

  protected override onEnvironmentChanged(): void {
    this.#followTheme();
    const next = this.desktop ?? null;
    if (next !== this.#desktop) {
      this.#desktop?.off("workareachanged", this.#onWorkAreaChanged);
      this.#desktop = next;
      this.#desktop?.on("workareachanged", this.#onWorkAreaChanged);
    }
    this.#followWorkArea();
    this.#applyFit();
  }

  /**
   * Scale the contents so a fixed design fills the room it is given.
   *
   * Done with `childScale`, the same transform a `ScrollViewer` zooms with, so text inside is
   * re-rasterized at the size it is drawn rather than magnified, and every clip stays
   * axis-aligned. It multiplies with the desktop's `uiScale` instead of fighting it: the desktop
   * already works in logical pixels, so a page at `fit="contain"` on a 1.25 desktop ends up at
   * 1.25 times whatever the fit decided.
   */
  #applyFit(): void {
    const design = this.design;
    if (!design || this.fit === "none" || design.width <= 0 || design.height <= 0) {
      if (this.childScale !== 1) {
        this.childScale = 1;
        this.performLayout();
      }
      return;
    }
    const scale =
      this.fit === "width"
        ? this.width / design.width
        : Math.min(this.width / design.width, this.height / design.height);
    if (this.childScale === scale) return;
    this.childScale = scale;
    this.layout();
  }

  protected override onResize(): void {
    this.#applyFit();
    super.onResize();
  }
}
